import { useState } from 'react';
import { Card } from 'components/ui/card';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  ExternalLink,
  InfoIcon,
} from 'lucide-react';
import { IFileVersions, IReferences } from 'modules/threads/types';
import Tooltip from 'components/ui/tooltip';
import { Icon } from 'ui';
import { Link } from 'react-router-dom';
import { useModal } from 'modules/modals/ModalProvider';
import { convertCentsToDollars } from 'utils/helpers';
import { FormattedDate } from 'components/dates';
import { capitalize } from 'lodash';
import { usePlyableAccessLinkMutation } from 'modules/playable/api';
import { showUncaughtErrorMessage } from 'modules/alert/utils';
import { isThreads3dFile } from 'pages/model/utils';
import { TEST_IDS } from 'mocks/ids';

const PlyableQuote = ({ version }: { version: IFileVersions }) => {
  const [getPlyableLink] = usePlyableAccessLinkMutation();

  // Retrieve magic link and navigate there in a new tab
  const navigateToPlyable = async () => {
    try {
      const response = await getPlyableLink({ referenceId: `${version.id}` }).unwrap();
      window.open(response.link, '_blank')?.focus();
    } catch (error) {
      if ((error as any).status_code !== 400) {
        showUncaughtErrorMessage(error);
      }
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded bg-background p-2 md:flex-row md:justify-between">
      <div className="flex flex-col gap-1 text-xs">
        <h4 className="mb-2 text-sm font-semibold">Quote Details</h4>
        <div>
          <span className="">Order Total:</span>{' '}
          <span className="italic">
            {convertCentsToDollars(version.plyablePriceInCents, version.plyablePriceCurrency) ||
              'N/A'}
          </span>
        </div>

        <div>
          <span className="">Order Status:</span>{' '}
          <span className="italic">{capitalize(version.plyableOrderStatus) || 'N/A'}</span>
        </div>

        <div>
          <span className="">Est. Delivery Date:</span>{' '}
          <span className="italic">
            <FormattedDate date={version.plyableEstimatedDeliveryDate} />
          </span>
        </div>
      </div>

      <div className="flex flex-row-reverse justify-between gap-1 md:flex-col md:items-end">
        <div className="rounded-full bg-blue-7 p-1 px-2 text-xs text-white">
          {capitalize(version.plyableQuoteFor)}
        </div>

        <div className="flex flex-row items-center gap-2">
          {version.isPlyableDfmCheckFailed && (
            <Tooltip content="Your mold design has failed one or more DFM checks">
              <InfoIcon className="h-5 w-5 text-app-red" />
            </Tooltip>
          )}
          <Tooltip
            content={
              !version.plyableUrl
                ? 'The quote for the model hasn’t been generated yet. Please wait.'
                : 'Open in Plyable'
            }
          >
            <button
              type="button"
              className="flex items-center justify-center self-center rounded bg-blue-2 p-1"
              onClick={navigateToPlyable}
              disabled={!version.plyableUrl}
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export const VersionHistory = ({ reference }: { reference: IReferences }) => {
  const { close } = useModal();
  const [plyableExpanded, setPlyableExpanded] = useState<number>();

  return (
    <Card className="flex flex-col gap-2 p-2 px-4">
      <h3 className="text-md font-semibold">Version History</h3>

      <div className="flex max-h-60 flex-col gap-3 overflow-auto">
        {reference.fileVersions.map((version, index) => {
          const fileName = URL.parse(version.file)?.pathname?.split('/').splice(-1)[0];
          const plyableOpen = plyableExpanded === index;
          return (
            <div className="flex w-full flex-col gap-2 rounded bg-secondary p-2" key={version.id}>
              <div className="flex w-full flex-col justify-between gap-2 md:flex-row md:items-center">
                <div className="flex min-w-0 flex-col gap-0">
                  <span className="text-sm font-semibold">
                    Version {version.version}{' '}
                    {index === 0 && <span className="italic"> (current)</span>}
                  </span>
                  <span className="truncate text-xs italic text-app-gray">{fileName}</span>
                </div>

                <div className="flex flex-row items-center gap-2 self-end md:self-center">
                  {/* TODO - We should be able to allow this for Plyable too */}
                  {isThreads3dFile(version.file) && (
                    <Tooltip content="View Threads3D Model">
                      <Link
                        to={`/model/${version.id}`}
                        onClick={() => close()}
                        className="flex flex-row items-center gap-1 rounded-sm bg-threads-3d p-[2px]"
                      >
                        <div
                          className="rounded-sm bg-dark1 p-1"
                          data-testid={TEST_IDS.threads_3d_version_history}
                        >
                          <Icon icon="Threads3dIcon" size="small" />
                        </div>

                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </Tooltip>
                  )}
                  {version.plyableQuoteFor && (
                    <Tooltip content={plyableOpen ? 'Hide Quote' : 'View Plyable Quote'}>
                      <button
                        type="button"
                        className="flex flex-row items-center gap-1 rounded-sm bg-plyable p-[2px]"
                        onClick={() =>
                          setPlyableExpanded((prev) => (prev === index ? undefined : index))
                        }
                      >
                        <div
                          className="rounded-sm bg-dark1 p-1"
                          data-testid={TEST_IDS.plyable_version_history}
                        >
                          <Icon icon="PlyableLogo" size="small" />
                        </div>

                        {plyableOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </Tooltip>
                  )}

                  <Tooltip content="Download Version">
                    <div className="flex items-center justify-center self-center rounded bg-blue-2 p-1">
                      <a href={version.file} target="_blank" rel="noreferrer">
                        <Download className="h-4 w-4" />
                      </a>
                    </div>
                  </Tooltip>
                </div>
              </div>
              {plyableOpen && <PlyableQuote version={version} />}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
